import type { CounselorAppointment } from '@/apiTypes'
import type Plan from '@/classes/Plan'
import type { Session } from '@/classes/Session'
import type { DocumentType } from '@/enums/DocumentType'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { useCrmStore } from '@/stores/crm'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import type { PlanBasicData, PlanData, PlanOption } from '@/types'
import { formatLastUpdated } from '@/views/NewSession/PlanVersions/utils'

export type PlanVersion = {
	id: string
	fullName: string
	lastUpdated?: string
	source: string
	funeralHomeId: string
	documents?: Array<{ id: string; type: DocumentType }>
	completedSteps?: Array<string>
}

/**
 * This is the union of the old CRM identifier fields (leadId) and the new Salesforce identifying fields
 */
export type CrmIdentifiers = {
	leadId?: string | null
	opportunityId?: string | null
	contactId?: string | null
}

// This helper is related to Plan Versions

export const usePlanVersionsV2 = () => {
	const planStore = usePlanStore()
	const cloudSyncService = useCloudSyncService()
	const crmStore = useCrmStore()
	const sessionStore = useSessionStore()

	const getLatestPlanVersionsFromAppointment = async (
		session?: Session,
		appointment?: CounselorAppointment
	) => {
		let planOptions = []

		if (!appointment && session?.appointmentId) {
			appointment = crmStore.findAppointment(session.appointmentId)
		}

		if (!session && appointment) {
			session = sessionStore.getSessionForAppointment(appointment.id)
		}

		if (session?.planIds) {
			planOptions.push(...(await getLocalPrintVersions(session.planIds)))
		}

		if (appointment?.lead?.data?.id || appointment?.opportunityId) {
			planOptions.push(
				...(await getCloudSyncPlanVersions({
					leadId: appointment?.lead?.data?.id,
					opportunityId: appointment?.opportunityId,
				}))
			)
		}

		// Group plan options by fullName
		return planOptions.reduce(groupNewestByName, {})
	}

	// grab all the stored plans, hydrate them for filling in mem guide
	const loadPlansFromPlanVersions = async (
		versions: Array<PlanVersion>
	): Promise<Array<Plan>> => {
		const plans: Plan[] = []

		for (const version of versions) {
			if (version.source === 'local') {
				plans.push(await planStore.loadPlan(version.id))
			}

			if (version.source === 'cloud-sync') {
				// To hydrate a plan from cloudSync we'd have to have its DEK in the local keystore
				// TODO: Avoid key clobbering in PSS-1936
				await cloudSyncService.plan.loadEncryptionKeyById(version.id)

				const hydratedCloudSyncPlan = await cloudSyncService.plan.getById(
					version.id
				)
				if (
					!hydratedCloudSyncPlan ||
					hydratedCloudSyncPlan.encryptionStatus !== 'unencrypted'
				) {
					throw new Error(
						`Error occurred while fetching cloud sync plan for ${version.id}`
					)
				}
				plans.push(hydratedCloudSyncPlan)
			}
		}

		return plans
	}

	/**
	 * This is for printing plan versions on the home dashboard
	 * @param planIds
	 */
	const getLocalPrintVersions = async (
		planIds: string[]
	): Promise<PlanVersion[]> => {
		const localPlansBasicData = await Promise.all(
			planIds.map((planId) => {
				return planStore.loadPlanBasicData(planId)
			})
		)

		return localPlansBasicData.map((planBasicData) =>
			mapPlanBasicDataToPlanVersion(planBasicData, 'local')
		)
	}

	/**
	 * Retrieves matching cloud sync versions by lead/opportunity/contact
	 */
	const getCloudSyncPlanVersions = async (
		identifiers: Partial<PlanData> | CrmIdentifiers
	): Promise<PlanVersion[]> => {
		const cloudSyncPlans =
			await cloudSyncService.plan.getByLeadIdOrOpportunityIdOrContactId({
				leadId: identifiers.leadId,
				opportunityId: identifiers.opportunityId,
				contactId: identifiers.contactId,
			})

		return cloudSyncPlans.map((plan) => {
			return mapPlanBasicDataToPlanVersion(plan.planData.basic, 'cloud-sync')
		})
	}

	/**
	 * Retrieves matching LocalForage versions by lead/opportunity/contact
	 */
	const getLocalPlanVersions = async (
		partialPlan: Partial<PlanData>
	): Promise<PlanVersion[]> => {
		const localRecords = await planStore.getByLeadIdOrOpportunityIdOrContactId({
			leadId: partialPlan.leadId,
			opportunityId: partialPlan.opportunityId,
			contactId: partialPlan.contactId,
		})

		return localRecords.map((plan) => {
			return mapPlanBasicDataToPlanVersion(plan.basic, 'local')
		})
	}

	/**
	 * Give it a plan or partial plan data that includes things like leadId or opportunity ID, and it will find the
	 * plan versions across multiple sources.
	 * @param planData
	 */
	const getRelatedPlanVersions = async (
		planData: Partial<PlanData>
	): Promise<PlanVersion[]> => {
		const localVersions = await getLocalPlanVersions(planData)
		const cloudSyncVersions = await getCloudSyncPlanVersions(planData)
		return [...localVersions, ...cloudSyncVersions]
	}

	/**
	 * Add a plan to the map under the fullName if it is newer.
	 * @param aggregate
	 * @param currentValue
	 */
	const groupNewestByName = (
		aggregate: Record<string, PlanVersion>,
		currentValue: PlanVersion
	): Record<string, PlanVersion> => {
		if (!currentValue.fullName) {
			return aggregate
		}

		if (!aggregate[currentValue.fullName]) {
			aggregate[currentValue.fullName] = currentValue
			return aggregate
		}

		const currentIsNewer =
			Date.parse(currentValue.lastUpdated!) >
			Date.parse(aggregate[currentValue.fullName].lastUpdated!)
		if (currentIsNewer) {
			aggregate[currentValue.fullName] = currentValue
		}

		return aggregate
	}

	const getFullName = (plan: Partial<PlanData>) => {
		const fullName = (plan.firstName ?? '') + ' ' + (plan.lastName ?? '')
		return fullName.trim()
	}

	const mapPlanVersionToOption = (planVersion: PlanVersion): PlanOption => {
		return {
			id: planVersion.id,
			fullName: planVersion.fullName,
			lastUpdated: planVersion.lastUpdated,
			formattedDate: formatLastUpdated(planVersion.lastUpdated),
			source: planVersion.source,
			funeralHomeId: planVersion.funeralHomeId,
		}
	}

	/**
	 * Takes in PlanBasicData and returns a PlanVersion
	 * @param planBasicData
	 * @param source
	 */
	const mapPlanBasicDataToPlanVersion = (
		planBasicData: Partial<PlanBasicData> & Pick<PlanData, 'id'>,
		source: string
	): PlanVersion => {
		return {
			id: planBasicData.id,
			fullName: getFullName(planBasicData),
			lastUpdated: planBasicData.lastUpdated,
			funeralHomeId: planBasicData.funeralHomeId ?? '',
			source,
			completedSteps: planBasicData.completedSteps,
			documents: planBasicData.documents,
		}
	}

	/**
	 * Checks if a PlanVersion is the newest version of this plan, of all PlanVersions
	 * in an array.  You can have unrelated plans in the version list, they will be ignored
	 * if the plan_ids are different.
	 * @param currentVersion The specific version you want to check
	 * @param allVersions The array of versions you want to compare it to
	 */
	const isNewestVersion = (
		currentVersion: PlanVersion,
		allVersions: PlanVersion[]
	) => {
		return !allVersions.some((planVersion) =>
			sameButFirstIsNewer(planVersion, currentVersion)
		)
	}

	const sameButFirstIsNewer = (
		firstPlanVersion: PlanVersion,
		secondPlanVersion: PlanVersion
	) => {
		// avoid comparing with itself
		if (firstPlanVersion === secondPlanVersion) {
			return false
		}
		// it's not the same plan
		if (
			firstPlanVersion.id.toUpperCase() !== secondPlanVersion.id.toUpperCase()
		) {
			return false
		}

		const firstPlanVersionTime = Date.parse(firstPlanVersion.lastUpdated!)
		const secondPlanVersionTime = Date.parse(secondPlanVersion.lastUpdated!)

		// if id collides and the lastUpdated time is identical,
		// consider the local one newer
		return (
			firstPlanVersionTime > secondPlanVersionTime ||
			(firstPlanVersionTime === secondPlanVersionTime &&
				secondPlanVersion.source === 'cloud-sync' &&
				firstPlanVersion.source === 'local')
		)
	}

	const isSamePerson = (planVersion: PlanVersion, plan: Partial<PlanData>) => {
		return (
			planVersion.fullName.trim().toLowerCase() ===
			getFullName(plan).trim().toLowerCase()
		)
	}

	/**
	 * Hydrates the full PlanBasicData for a PlanVersion, looking in the store matching
	 * its source.
	 * @param planVersion
	 */
	const getPlanBasicDataForVersion = async (
		planVersion: PlanVersion
	): Promise<(Partial<PlanBasicData> & Pick<PlanData, 'id'>) | undefined> => {
		if (planVersion.source === 'local') {
			return await planStore.loadPlanBasicData(planVersion.id)
		}

		if (planVersion.source === 'cloud-sync') {
			return await cloudSyncService.plan.getBasicDataById(planVersion.id)
		}
	}

	/**
	 * Finds the newest related plan version per person, for people related to this
	 * LeadId/OpportunityId/ContactId who are not already in currentPlans.
	 *
	 * The returned versions are not hydrated - callers hydrate the one they need
	 * via getPlanBasicDataForVersion when the participant is actually added.
	 */
	const getUnusedParticipants = async (
		identifiers: CrmIdentifiers,
		currentPlans: Partial<PlanData>[]
	): Promise<Record<string, PlanVersion>> => {
		const relatedVersions = await getRelatedPlanVersions(identifiers)

		const unusedVersions = relatedVersions.filter(
			(version) => !currentPlans.some((plan) => isSamePerson(version, plan))
		)

		return unusedVersions.reduce(
			groupNewestByName,
			{} as Record<string, PlanVersion>
		)
	}

	return {
		getLatestPlanVersionsFromAppointment,
		loadPlansFromPlanVersions,
		getCloudSyncPlanVersions,
		getRelatedPlanVersions,
		getPlanBasicDataForVersion,
		mapPlanVersionToOption,
		isNewestVersion,
		getUnusedParticipants,
	}
}
